import {attemptSessionId, versionFor, type CompetenceLevel, type LibraryState, type StudentAttempt} from "./activity-library";
import type {CatalogCompetency} from "./catalog";

type Level = "A" | "B" | "C" | "D";
const scores: Record<Level, number> = {A: 20, B: 13.3, C: 6.7, D: 0};

export type DashboardActivityCompetency = {
  competency: CatalogCompetency;
  level: CompetenceLevel;
};

export type DashboardDigitalActivitySummary = {
  attempt: StudentAttempt;
  title: string;
  chapter: string;
  results: DashboardActivityCompetency[];
  grade: number | null;
};

export function digitalActivitySummaryForSession(
  state: LibraryState | undefined,
  digitalSessionId: string | undefined,
  catalog: CatalogCompetency[],
): DashboardDigitalActivitySummary | null {
  if (!state || !digitalSessionId) return null;
  const attempt = state.attempts.find((item) => attemptSessionId(item) === digitalSessionId);
  if (!attempt) return null;
  const version = versionFor(state, attempt);
  if (!version) return null;

  const seen = new Set<string>();
  const results: DashboardActivityCompetency[] = [];

  for (const checkpoint of version.checkpoints) {
    if (seen.has(checkpoint.competencyId)) continue;
    seen.add(checkpoint.competencyId);
    const competency = catalog.find((item) => item.id === checkpoint.competencyId);
    if (!competency) continue;
    const matchingResults = version.checkpoints
      .filter((item) => item.competencyId === checkpoint.competencyId)
      .map((item) => attempt.competenceResults[item.id])
      .filter(Boolean);
    const result = matchingResults.at(-1);
    results.push({competency, level: result?.level ?? "NE"});
  }

  for (const competencyId of version.competenceIds) {
    if (seen.has(competencyId)) continue;
    const competency = catalog.find((item) => item.id === competencyId);
    if (!competency) continue;
    seen.add(competencyId);
    const result = Object.values(attempt.competenceResults)
      .filter((item) => item.competencyId === competencyId)
      .sort((a, b) => a.validatedAt.localeCompare(b.validatedAt))
      .at(-1);
    results.push({competency, level: result?.level ?? "NE"});
  }

  const scored = results.filter(
    (item): item is DashboardActivityCompetency & {level: Level} =>
      ["A", "B", "C", "D"].includes(item.level),
  );
  const calculatedGrade = scored.length
    ? scored.reduce((sum, item) => sum + scores[item.level], 0) / scored.length
    : null;

  return {
    attempt,
    title: version.title,
    chapter: version.chapterTitle,
    results,
    grade: attempt.grade ?? calculatedGrade,
  };
}
